import React, { memo } from 'react';

/**
 * HighlightCard - Shared component for player spotlight cards
 *
 * Used by LeaderboardHero and QuotasHero to display player achievements
 * with consistent styling and accessibility.
 */
const HighlightCard = memo(({
  title,
  subtitle,
  backgroundImage,
  player,
  playerImageSrc,
  stat,
  statLabel,
  accentColor,
  statColorClass,
  onPlayerClick,
}) => {
  if (!player) return null;

  const handleClick = () => {
    if (onPlayerClick) onPlayerClick(player);
  };

  const isClickable = !!onPlayerClick;

  // Default stat color if not provided
  const statColor = statColorClass || 'text-gray-800 dark:text-gray-200';

  return (
    <div className="relative overflow-hidden rounded-xl p-4 bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 shadow-sm hover:shadow-md transition-shadow">
      {/* Background watermark image */}
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className="absolute -right-4 -bottom-4 w-28 h-28 opacity-[0.15] dark:opacity-[0.2] object-contain pointer-events-none"
        />
      )}

      {/* Accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${accentColor}`} />

      {/* Title & Subtitle */}
      <div className="mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {subtitle}
          </p>
        )}
      </div>

      {/* Player Info */}
      <div className="flex items-center gap-3">
        {isClickable ? (
          <button
            type="button"
            onClick={handleClick}
            aria-label={`View details for ${player.name}`}
            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-forest-500 rounded-full"
          >
            <img
              src={playerImageSrc}
              alt={player.name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-white dark:ring-navy-700 shadow-sm hover:scale-105 transition-transform"
            />
          </button>
        ) : (
          <div className="flex-shrink-0">
            <img
              src={playerImageSrc}
              alt={player.name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-white dark:ring-navy-700 shadow-sm"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {isClickable ? (
            <button
              type="button"
              onClick={handleClick}
              className="font-bold text-gray-900 dark:text-white truncate hover:text-forest-600 dark:hover:text-forest-400 transition-colors text-left"
            >
              {player.name}
            </button>
          ) : (
            <div className="font-bold text-gray-900 dark:text-white truncate">
              {player.name}
            </div>
          )}
          <div className="flex items-baseline gap-1">
            <span className={`text-xl font-bold tabular-nums ${statColor}`}>
              {stat}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {statLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

HighlightCard.displayName = 'HighlightCard';

export default HighlightCard;
